
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Layout } from './components/Layout';
import { Auth } from './components/views/Auth';
import { DataHistorySection } from './components/views/DataHistorySection';
import { Button } from './components/ui/Button';
import { DatePicker } from './components/ui/DatePicker';
import { UserProfile, AppView } from './types';
import { Link, Copy, Share, Phone, Info, Pencil, Check, X, Palette, Shield, Loader2, UserX, Mail, Calendar, Baby, Download, Trash2 } from 'lucide-react';
import { authApi, userApi, syncApi } from './services/apiService';
// Type-only import for PartnerRoomConnection (actual code loaded dynamically)
import type { PartnerRoomConnection } from './services/realtimeService';
import { QRCodeSVG } from 'qrcode.react';

// Eagerly load core nav views for instant navigation
import { Dashboard } from './components/views/Dashboard';
import { Journey } from './components/views/Journey';
import { Tools } from './components/views/Tools';
import { Community } from './components/views/Community';

// Lazy load views that aren't immediately needed
const Onboarding = lazy(() => import('./components/views/Onboarding').then(m => ({ default: m.Onboarding })));
const PartnerCoach = lazy(() => import('./components/views/PartnerCoach').then(m => ({ default: m.PartnerCoach })));
const LaborMode = lazy(() => import('./components/views/LaborMode').then(m => ({ default: m.LaborMode })));
const Legal = lazy(() => import('./components/views/Legal').then(m => ({ default: m.Legal })));
const Admin = lazy(() => import('./components/views/Admin').then(m => ({ default: m.Admin })));
// LinkRedirect is only used when opening invite links
const LinkRedirect = lazy(() => import('./components/views/LinkRedirect').then(m => ({ default: m.LinkRedirect })));

// Loading fallback component
const ViewLoader = () => (
  <div className="h-full flex items-center justify-center bg-stone-50">
    <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
  </div>
);

function App() {
  const [view, setView] = useState<AppView>(AppView.AUTH);
  const [viewResetKey, setViewResetKey] = useState(0);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  const [authName, setAuthName] = useState<string | null>(null);
  const [emergencyContact, setEmergencyContact] = useState(user?.emergencyContact || '');
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialTool, setInitialTool] = useState<string | undefined>(undefined);
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(null);

  // Profile Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    partnerName: '',
    dueDate: '',
    comparisonTheme: 'fruit'
  });

  // Helper for current date
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    // Initialize app - check for auth and load user
    initializeApp();
  }, []);

  // Sync emergency contact from user profile when loaded
  useEffect(() => {
    if (user?.emergencyContact) {
      setEmergencyContact(user.emergencyContact);
    }
  }, [user?.emergencyContact]);

  // PartnerRoom connection ref for real-time sync
  const partnerRoomRef = React.useRef<PartnerRoomConnection | null>(null);

  // Connect to PartnerRoom WebSocket when linked for real-time sync
  useEffect(() => {
    if (!user?.isLinked || !user?.id) {
      // Disconnect if not linked
      partnerRoomRef.current?.disconnect();
      partnerRoomRef.current = null;
      return;
    }

    // Dynamic import to reduce initial bundle - only load when partner is linked
    let isMounted = true;
    import('./services/realtimeService').then(({ createPartnerRoomConnection }) => {
      if (!isMounted || !user?.isLinked || !user?.id) return;

      // Create couple ID from both user IDs (sorted for consistency)
      const coupleId = [user.id, user.linkedPartnerId || ''].sort().join('-');

      partnerRoomRef.current = createPartnerRoomConnection(
        coupleId,
        user.id,
        user.name,
        user.role,
        {
          onStateChange: () => { },
          onPartnerJoined: (name) => console.log(`${name} is online`),
          onPartnerLeft: (name) => console.log(`${name} went offline`),
          onPartnerView: () => { },
          onNameLiked: () => { },
          onNameMatched: () => { },
          onFactSaved: () => { },
          onFactUnsaved: () => { },
          onKickLogged: () => { },
          onPartnerUnlinked: async () => {
            // Partner unlinked us - refresh our profile
            try {
              const profile = await userApi.getProfile();
              setUser(profile);
            } catch (error) {
              console.error('Failed to refresh profile after unlink:', error);
            }
          },
          onCheckinCompleted: (by, mood) => {
            console.log(`${by} completed check-in: ${mood}`);
          },
          onProfileUpdated: (by, changes) => {
            console.log(`${by} updated profile:`, changes);
          },
          onAppointmentAdded: (by, title, date) => {
            console.log(`${by} added appointment: ${title} on ${date}`);
          },
          onTodoAdded: (by, id, text, category) => {
            console.log(`${by} added todo: ${text}`);
          },
          onTodoUpdated: (by, id, isCompleted) => {
            console.log(`${by} ${isCompleted ? 'completed' : 'uncompleted'} a todo`);
          },
          onTodoDeleted: (by, id) => {
            console.log(`${by} deleted a todo`);
          },
          onDataUpdated: (dataType, by) => {
            console.log(`${by} updated ${dataType} - consider refreshing`);
            // Components can listen for this via a custom event to auto-refresh
            window.dispatchEvent(new CustomEvent('partner-data-updated', { detail: { dataType, by } }));
          },
          onStatusChange: () => { },
        }
      );

      partnerRoomRef.current.connect();
    });

    // Listen for data updates from components and forward to WebSocket
    const handleNotifyPartner = (e: Event) => {
      const customEvent = e as CustomEvent<{ dataType: string }>;
      partnerRoomRef.current?.notifyDataUpdated(customEvent.detail.dataType);
    };
    window.addEventListener('notify-partner-data-update', handleNotifyPartner);

    return () => {
      isMounted = false;
      window.removeEventListener('notify-partner-data-update', handleNotifyPartner);
      partnerRoomRef.current?.disconnect();
      partnerRoomRef.current = null;
    };
  }, [user?.isLinked, user?.id, user?.linkedPartnerId, user?.name, user?.role]);

  const initializeApp = async () => {
    setIsLoading(true);

    // Check URL for magic link token or join code
    const urlParams = new URLSearchParams(window.location.search);
    const magicToken = urlParams.get('token');
    const joinCode = urlParams.get('join');

    // Store join code for after auth if present
    if (joinCode) {
      setPendingJoinCode(joinCode.toUpperCase());
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (magicToken) {
      try {
        const result = await authApi.verify(magicToken);
        // Clear token from URL
        window.history.replaceState({}, '', window.location.pathname);

        if (result.isNewUser || !result.user.name) {
          setAuthEmail(result.user.email || null);
          setView(AppView.ONBOARDING);
        } else {
          setUser(result.user as UserProfile);
          setView(AppView.DASHBOARD);
        }
      } catch (error) {
        console.error('Magic link verification failed:', error);
        setView(AppView.AUTH);
      }
    } else if (authApi.isAuthenticated()) {
      // Already have a session, load user profile
      try {
        const profile = await userApi.getProfile();

        if (!profile.isOnboarded) {
          setAuthEmail(profile.email || null);
          setView(AppView.ONBOARDING);
        } else {
          setUser(profile);
          setView(AppView.DASHBOARD);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        // Session invalid, show auth
        authApi.logout();
        setView(AppView.AUTH);
      }
    } else {
      // No session, show Auth
      setView(AppView.AUTH);
    }

    setIsLoading(false);
  };

  const handleLogin = async (result: { success: boolean; isNewUser: boolean; user: UserProfile }) => {
    // Check if user needs onboarding
    if (result.isNewUser || !result.user.name || !result.user.dueDate) {
      setAuthEmail(result.user.email || '');
      setAuthName(result.user.name || null);  // Store Google name for prefill
      setView(AppView.ONBOARDING);
    } else {
      // User is already onboarded, go directly to dashboard
      setUser(result.user);
      setView(AppView.DASHBOARD);
    }
  };

  const handleOnboardingComplete = async (profile: UserProfile) => {
    try {
      // Save profile to backend
      const savedProfile = await userApi.completeOnboarding({
        name: profile.name,
        partnerName: profile.partnerName,
        role: profile.role,
        dueDate: profile.dueDate,
        focusAreas: profile.focusAreas,
        comparisonTheme: profile.comparisonTheme,
      });

      setUser(savedProfile);
      setView(AppView.DASHBOARD);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Fallback to local storage if API fails
      const finalProfile = { ...profile, email: authEmail || undefined };
      setUser(finalProfile);
      localStorage.setItem('coexpect_user', JSON.stringify(finalProfile));
      setView(AppView.DASHBOARD);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    setAuthEmail(null);
    setView(AppView.AUTH);

    // Disable Google One Tap auto-sign-in to prevent immediate re-login
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const generateCodeInSettings = async () => {
    if (!user || isGeneratingCode) return;
    setIsGeneratingCode(true);
    try {
      const { code } = await syncApi.generateInviteCode();
      const updatedUser = { ...user, inviteCode: code };
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to generate invite code:', error);
      alert('Failed to generate invite code. Please try again.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const shareCode = async () => {
    if (!user?.inviteCode) return;
    const deepLink = `https://coexpect.com/join/${user.inviteCode}`;
    const shareData = {
      title: 'Join me on CoExpect',
      text: `Let's track our pregnancy journey together on CoExpect! Tap to connect instantly:`,
      url: deepLink
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share canceled');
      }
    } else {
      navigator.clipboard.writeText(`${shareData.text} ${deepLink}`);
      alert('Invite link copied!');
    }
  };

  // Profile Edit Handlers
  const startEditing = () => {
    if (!user) return;
    setEditFormData({
      name: user.name,
      email: user.email || authEmail || '',
      partnerName: user.partnerName,
      dueDate: user.dueDate,
      comparisonTheme: user.comparisonTheme || 'fruit'
    });
    setIsEditing(true);
  };

  const saveProfile = async () => {
    if (!user) return;
    setIsSaving(true);

    try {
      const updatedUser = await userApi.updateProfile({
        name: editFormData.name,
        partnerName: editFormData.partnerName,
        dueDate: editFormData.dueDate,
        comparisonTheme: editFormData.comparisonTheme as any,
      });

      setUser(updatedUser);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save profile:', error);
      // Fallback to local update
      const updatedUser: UserProfile = {
        ...user,
        name: editFormData.name,
        email: editFormData.email,
        partnerName: editFormData.partnerName,
        dueDate: editFormData.dueDate,
        comparisonTheme: editFormData.comparisonTheme as any
      };
      setUser(updatedUser);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-brand-600 animate-spin mx-auto mb-4" />
          <p className="text-stone-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle reset when clicking on already-active tab
  const handleResetView = () => {
    setViewResetKey(prev => prev + 1);
    // Clear initialTool so Tools goes back to HUB when reset
    setInitialTool(undefined);
  };

  return (
    <Layout
      currentView={view}
      onChangeView={setView}
      onResetView={handleResetView}
      // Disable default layout scrolling for Labor Mode, Tools, Dashboard, and Legal
      disableScroll={view === AppView.LABOR_MODE || view === AppView.TOOLS || view === AppView.DASHBOARD || view === AppView.LEGAL}
    >
      {/* Deep link handler - shows when user opens /join/CODE link */}
      {pendingJoinCode ? (
        <div className="fixed inset-0 z-50">
          <Suspense fallback={<ViewLoader />}>
            <LinkRedirect
              code={pendingJoinCode}
              onSuccess={(updatedUser) => {
                setUser(updatedUser);
                setPendingJoinCode(null);
                setView(AppView.DASHBOARD);
              }}
              onNeedsAuth={(code) => {
                // Keep the code, show auth, will auto-connect after login
                setView(AppView.AUTH);
              }}
              onGoHome={() => {
                setPendingJoinCode(null);
                setView(AppView.AUTH);
              }}
            />
          </Suspense>
        </div>
      ) : null}

      {!pendingJoinCode && view === AppView.AUTH && (
        <Auth onLogin={handleLogin} />
      )}

      {view === AppView.ONBOARDING && (
        <Suspense fallback={<ViewLoader />}>
          <Onboarding
            onComplete={handleOnboardingComplete}
            initialName={authName || undefined}
            onViewLegal={() => setView(AppView.LEGAL)}
          />
        </Suspense>
      )}

      {view === AppView.DASHBOARD && user && (
        <Dashboard
          key={`dashboard-${viewResetKey}`}
          user={user}
          onNavigateToSettings={() => setView(AppView.SETTINGS)}
          onNavigateToTool={(tool) => {
            setInitialTool(tool);
            setView(AppView.TOOLS);
          }}
          onUserUpdate={setUser}
        />
      )}

      {view === AppView.JOURNEY && user && (
        <Journey key={`journey-${viewResetKey}`} user={user} />
      )}

      {view === AppView.TOOLS && user && (
        <Tools
          key={`tools-${viewResetKey}-${initialTool || 'hub'}`}
          user={user}
          initialTool={initialTool}
          onNavigateToLaborMode={() => setView(AppView.LABOR_MODE)}
          onNavigateToSettings={() => setView(AppView.SETTINGS)}
        />
      )}

      {view === AppView.COMMUNITY && user && (
        <Community key={`community-${viewResetKey}`} user={user} />
      )}

      {view === AppView.LABOR_MODE && user && (
        <Suspense fallback={<ViewLoader />}>
          <LaborMode key={`labor-${viewResetKey}`} user={user} />
        </Suspense>
      )}

      {view === AppView.LEGAL && (
        <Suspense fallback={<ViewLoader />}>
          <Legal onBack={() => setView(AppView.SETTINGS)} />
        </Suspense>
      )}

      {view === AppView.ADMIN && user && (
        <Suspense fallback={<ViewLoader />}>
          <Admin user={user} onBack={() => setView(AppView.SETTINGS)} />
        </Suspense>
      )}

      {view === AppView.SETTINGS && user && (
        <div className="p-6 space-y-6 animate-slide-up pb-32">
          <div className="pt-safe sm:pt-0 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-stone-900">Settings</h1>
            <button
              onClick={() => setView(AppView.DASHBOARD)}
              className="p-2 bg-stone-100 rounded-full text-stone-500 hover:bg-stone-200 transition-colors"
              aria-label="Close settings"
            >
              <X size={20} />
            </button>
          </div>

          {/* Account Card */}
          <div className="bg-white p-5 rounded-3xl border border-stone-100 shadow-sm transition-all duration-300">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-stone-900 text-lg">Your Profile</h3>
              {!isEditing && (
                <button
                  onClick={startEditing}
                  className="p-2 text-brand-600 hover:bg-brand-50 rounded-full transition-colors"
                  aria-label="Edit profile"
                >
                  <Pencil size={18} />
                </button>
              )}
            </div>

            {!isEditing ? (
              <div className="space-y-5">
                <div>
                  <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Your Name</label>
                  <p className="text-lg font-medium text-stone-900 mt-0.5">{user.name}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Email Address</label>
                  <p className="text-lg font-medium text-stone-900 mt-0.5">{user.email || authEmail}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Partner's Name</label>
                  <p className="text-lg font-medium text-stone-900 mt-0.5">{user.partnerName}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Expected Due Date</label>
                  <p className="text-lg font-medium text-stone-900 mt-0.5">
                    {user.dueDate && !isNaN(new Date(user.dueDate).getTime())
                      ? new Date(user.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Baby Size Theme</label>
                  <p className="text-lg font-medium text-stone-900 mt-0.5 capitalize">{user.comparisonTheme || 'Fruit'}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Your Name</label>
                  <input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-stone-50 border-2 border-stone-100 focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Email Address</label>
                  <input
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-stone-100 border-2 border-stone-100 text-stone-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-stone-400 mt-1 ml-1">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Partner's Name</label>
                  <input
                    value={editFormData.partnerName}
                    onChange={(e) => setEditFormData({ ...editFormData, partnerName: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-stone-50 border-2 border-stone-100 focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <DatePicker
                    label="Expected Due Date"
                    value={editFormData.dueDate}
                    onChange={(date) => setEditFormData({ ...editFormData, dueDate: date })}
                    minDate={todayString}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Baby Size Theme</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['fruit', 'sports', 'geek', 'tools', 'snacks'].map(theme => (
                      <button
                        key={theme}
                        onClick={() => setEditFormData({ ...editFormData, comparisonTheme: theme })}
                        className={`p-2 rounded-lg text-sm font-medium border-2 transition-all capitalize ${editFormData.comparisonTheme === theme ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-stone-100 text-stone-600 hover:border-stone-200'}`}
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button onClick={saveProfile} fullWidth className="gap-2" disabled={isSaving}>
                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />} Save Changes
                  </Button>
                  <Button onClick={() => setIsEditing(false)} variant="secondary" fullWidth className="gap-2">
                    <X size={18} /> Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Labor Emergency Contact */}
          <div className="bg-white p-5 rounded-3xl border border-stone-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-50 p-2.5 rounded-full text-red-600">
                <Phone size={20} />
              </div>
              <div>
                <h3 className="font-bold text-stone-900">Emergency Contact</h3>
                <p className="text-xs text-stone-500">One-tap dial during Labor Mode</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-stone-400 uppercase ml-1">Phone Number</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="tel"
                    value={emergencyContact}
                    onChange={(e) => setEmergencyContact(e.target.value)}
                    placeholder="Enter phone number"
                    className="flex-1 p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 font-mono focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all"
                  />
                  <button
                    onClick={async () => {
                      setIsSavingContact(true);
                      try {
                        await userApi.updateProfile({ emergencyContact });
                        setUser(prev => prev ? { ...prev, emergencyContact } : prev);
                        const btn = document.getElementById('save-contact-btn');
                        if (btn) {
                          btn.textContent = 'Saved!';
                          btn.classList.add('bg-emerald-600');
                          setTimeout(() => {
                            btn.textContent = 'Save';
                            btn.classList.remove('bg-emerald-600');
                          }, 2000);
                        }
                      } catch (error) {
                        console.error('Failed to save emergency contact:', error);
                        alert('Failed to save. Please try again.');
                      } finally {
                        setIsSavingContact(false);
                      }
                    }}
                    disabled={isSavingContact}
                    id="save-contact-btn"
                    className="px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {isSavingContact ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-50 p-3 rounded-lg border border-stone-100">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <span>Powers the SOS button in Labor Mode for quick emergency access.</span>
              </div>
            </div>
          </div>

          {/* Partner Sync */}
          <div className={`p-5 rounded-3xl border ${user.isLinked ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-stone-100 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2.5 rounded-full ${user.isLinked ? 'bg-emerald-200 text-emerald-700' : 'bg-brand-100 text-brand-600'}`}>
                <Link size={20} />
              </div>
              <div>
                <h3 className="font-bold text-stone-900">{user.isLinked ? 'Synced with Partner' : 'Partner Sync'}</h3>
                <p className="text-xs text-stone-500">
                  {user.isLinked
                    ? `Connected with ${user.linkedPartnerName || user.partnerName}`
                    : 'Share your pregnancy journey together'}
                </p>
              </div>
            </div>

            {user.isLinked ? (
              <div className="mt-4 space-y-4">
                {/* Partner Details */}
                <div className="bg-white/70 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-emerald-600" />
                    <div>
                      <p className="text-xs text-stone-500">Partner's Email</p>
                      <p className="text-sm font-medium text-stone-800">{user.linkedPartnerEmail || 'Not available'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Baby size={16} className="text-emerald-600" />
                    <div>
                      <p className="text-xs text-stone-500">Partner's Role</p>
                      <p className="text-sm font-medium text-stone-800 capitalize">{user.linkedPartnerRole === 'pregnant' ? 'Carrying Parent' : 'Partner'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar size={16} className="text-emerald-600" />
                    <div>
                      <p className="text-xs text-stone-500">Partner's Due Date</p>
                      <p className="text-sm font-medium text-stone-800">
                        {user.linkedPartnerDueDate && !isNaN(new Date(user.linkedPartnerDueDate).getTime())
                          ? new Date(user.linkedPartnerDueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                          : 'Not set'}
                      </p>
                    </div>
                  </div>
                  {user.linkedAt && (
                    <p className="text-xs text-emerald-600 pt-2 border-t border-emerald-100">
                      Connected since {new Date(user.linkedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>

                {/* Shared Features Info */}
                <div className="bg-white/70 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Share size={14} className="text-emerald-600" />
                    <h4 className="text-sm font-semibold text-stone-800">Synced Features</h4>
                  </div>
                  <p className="text-xs text-stone-600 mb-2">
                    The following features stay in sync with {user.linkedPartnerName || user.partnerName}:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Birth Plan', 'Hospital Bag', 'Names', 'Facts', 'Reminders', 'Checklists'].map(feature => (
                      <span key={feature} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Data History / Revert */}
                <DataHistorySection />

                {/* Unlink Button */}
                <button
                  onClick={() => setShowUnlinkConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-medium"
                >
                  <UserX size={18} />
                  Unlink Partner
                </button>

                {/* Unlink Confirmation Dialog */}
                {showUnlinkConfirm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUnlinkConfirm(false)}>
                    <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl animate-scale-in" onClick={e => e.stopPropagation()}>
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <UserX size={28} className="text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-stone-900">Unlink Partner?</h3>
                        <p className="text-sm text-stone-600 mt-2">
                          This will disconnect both you and <strong>{user.linkedPartnerName}</strong> from the shared journey. You can always reconnect later with a new invite code.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowUnlinkConfirm(false)}
                          className="flex-1 py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            setIsUnlinking(true);
                            try {
                              // Notify partner via WebSocket before API call
                              partnerRoomRef.current?.notifyUnlink();
                              await syncApi.unlinkPartner();
                              const updatedUser = await userApi.getProfile();
                              setUser(updatedUser);
                              setShowUnlinkConfirm(false);
                            } catch (error) {
                              console.error('Failed to unlink:', error);
                              alert('Failed to unlink partner. Please try again.');
                            } finally {
                              setIsUnlinking(false);
                            }
                          }}
                          disabled={isUnlinking}
                          className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {isUnlinking ? <Loader2 size={18} className="animate-spin" /> : <UserX size={18} />}
                          {isUnlinking ? 'Unlinking...' : 'Unlink'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-3 space-y-4">
                <p className="text-sm text-stone-600">
                  Share your code or QR with your partner for instant connection. They'll be linked in seconds!
                </p>
                {user.inviteCode ? (
                  <div className="space-y-4">
                    {/* QR Code Display */}
                    <div className="flex justify-center">
                      <div className="bg-white p-4 rounded-2xl border-2 border-brand-200 shadow-sm">
                        <QRCodeSVG
                          value={`https://coexpect.com/join/${user.inviteCode}`}
                          size={160}
                          level="M"
                          bgColor="#ffffff"
                          fgColor="#1e293b"
                          includeMargin={false}
                        />
                      </div>
                    </div>

                    {/* Code Display */}
                    <div className="bg-stone-100 rounded-xl p-4 text-center">
                      <p className="text-xs text-stone-500 font-medium mb-1">Your Invite Code</p>
                      <span className="font-mono font-bold text-2xl tracking-widest text-stone-800">{user.inviteCode}</span>
                    </div>

                    {/* Share Button */}
                    <Button
                      onClick={shareCode}
                      fullWidth
                      size="lg"
                      className="gap-2"
                    >
                      <Share size={20} />
                      Share with Partner
                    </Button>

                    {/* Deep Link Display */}
                    <div className="text-center">
                      <p className="text-xs text-stone-400 mb-1">Or send this link directly:</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://coexpect.com/join/${user.inviteCode}`);
                          const el = document.getElementById('copy-link-toast');
                          if (el) {
                            el.classList.remove('opacity-0');
                            setTimeout(() => el.classList.add('opacity-0'), 2000);
                          }
                        }}
                        className="text-sm text-brand-600 hover:text-brand-700 underline underline-offset-2"
                      >
                        coexpect.com/join/{user.inviteCode}
                      </button>
                      <p id="copy-link-toast" className="text-xs text-emerald-600 mt-1 opacity-0 transition-opacity">Link copied!</p>
                    </div>

                    {/* Regenerate Code Button */}
                    <div className="pt-2 border-t border-stone-100">
                      <button
                        onClick={generateCodeInSettings}
                        disabled={isGeneratingCode}
                        className="w-full flex items-center justify-center gap-2 py-2.5 text-stone-600 bg-stone-50 border border-stone-200 rounded-xl hover:bg-stone-100 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {isGeneratingCode ? (
                          <><Loader2 size={16} className="animate-spin" /> Generating...</>
                        ) : (
                          <><Link size={16} /> Generate New Code</>
                        )}
                      </button>
                      <p className="text-xs text-stone-400 text-center mt-2">
                        Old codes expire when you generate a new one
                      </p>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={generateCodeInSettings}
                    fullWidth
                    size="lg"
                    className="gap-2"
                    disabled={isGeneratingCode}
                  >
                    {isGeneratingCode ? <Loader2 size={20} className="animate-spin" /> : <Link size={20} />}
                    {isGeneratingCode ? 'Generating...' : 'Generate Invite Code'}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Legal Link */}
          <button
            onClick={() => setView(AppView.LEGAL)}
            className="w-full flex items-center justify-between p-4 bg-white border border-stone-100 rounded-2xl hover:bg-stone-50 transition-colors shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="bg-stone-100 p-2.5 rounded-full text-stone-500">
                <Shield size={18} />
              </div>
              <div className="text-left">
                <span className="font-bold text-stone-700 block">Privacy & Terms</span>
                <span className="text-xs text-stone-500">View our policies</span>
              </div>
            </div>
            <X className="rotate-45 text-stone-300" />
          </button>

          {/* Admin Panel Link - only for admin email */}
          {user.email === 'ksdog317@gmail.com' && (
            <button
              onClick={() => setView(AppView.ADMIN)}
              className="w-full flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-2xl hover:bg-amber-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-amber-200 p-2.5 rounded-full text-amber-700">
                  <Shield size={18} />
                </div>
                <div className="text-left">
                  <span className="font-bold text-amber-900 block">Admin Panel</span>
                  <span className="text-xs text-amber-700">Manage users & content</span>
                </div>
              </div>
              <X className="rotate-45 text-amber-400" />
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full py-4 text-red-600 font-semibold bg-red-50 rounded-2xl hover:bg-red-100 active:scale-[0.98] transition-all border border-red-100"
          >
            Sign Out
          </button>

          {/* About Section */}
          <div className="bg-gradient-to-br from-brand-50 to-stone-50 p-5 rounded-3xl border border-brand-100">
            <h3 className="font-bold text-stone-900 mb-2">About CoExpect</h3>
            <p className="text-sm text-stone-600 leading-relaxed">
              CoExpect is your trusted companion through pregnancy. We provide personalized guidance, tools, and community support tailored to your journey—whether you're the one carrying or the supportive partner.
            </p>
            <p className="text-sm text-stone-600 leading-relaxed mt-2">
              Built with privacy at the core. Your data stays secure and we never sell your information.
            </p>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-brand-100">
              <Mail size={14} className="text-brand-600" />
              <a href="mailto:support@coexpect.com" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
                support@coexpect.com
              </a>
            </div>
            <p className="text-xs text-stone-400 mt-3">Version 1.0 • Made with ❤️ for growing families</p>
          </div>

          {/* Data Management Section */}
          <div className="bg-white p-5 rounded-3xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-full bg-indigo-100 text-indigo-600">
                <Shield size={20} />
              </div>
              <div>
                <h3 className="font-bold text-stone-900">Your Data</h3>
                <p className="text-xs text-stone-500">GDPR/CCPA Rights</p>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  setIsExportingData(true);
                  try {
                    const blob = await userApi.exportData();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `coexpect-data-export-${new Date().toISOString().split('T')[0]}.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (error) {
                    alert('Failed to export data. Please try again.');
                  } finally {
                    setIsExportingData(false);
                  }
                }}
                disabled={isExportingData}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {isExportingData ? (
                  <><Loader2 size={18} className="animate-spin" /> Exporting...</>
                ) : (
                  <><Download size={18} /> Download My Data</>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white text-red-600 border border-red-200 rounded-xl font-medium hover:bg-red-50 transition-colors"
              >
                <Trash2 size={18} /> Delete My Account
              </button>
            </div>
          </div>

          {/* Delete Account Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="text-center mb-4">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-bold text-lg text-stone-900">Delete Your Account?</h3>
                  <p className="text-sm text-stone-600 mt-2">
                    This will permanently delete all your data including appointments, check-ins, photos, and messages. This action cannot be undone.
                  </p>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={async () => {
                      setIsDeletingAccount(true);
                      try {
                        await userApi.deleteAccount();
                        alert('Your account and all data has been deleted.');
                        handleLogout();
                      } catch (error) {
                        alert('Failed to delete account. Please try again.');
                      } finally {
                        setIsDeletingAccount(false);
                        setShowDeleteConfirm(false);
                      }
                    }}
                    disabled={isDeletingAccount}
                    className="w-full py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {isDeletingAccount ? 'Deleting...' : 'Yes, Delete Everything'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeletingAccount}
                    className="w-full py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

export default App;
